<template>
  <div
    class="MiniCartProduct w-full"
    :class="{
      'last:border-none border-b border-border-subtle py-12 desk:py-16 first:pt-0': !disableInteraction || isPalletDiscount,
      'pt-8 first:pt-0 last:pb-8': disableInteraction && !isPalletDiscount
    }"
  >
    <!-- Product Info -->
    <div
      v-if="!disableInteraction && isCheckout && item.quantity > item.onHandValue && !hideMeta && neededArea > 0"
      class="w-full bg-bg-attention p-8 desk:p-12 mb-12"
    >
      <div class="flex type-xs gap-8">
        <GlobalIcon name="info" size="xsmall" />
        <div class="flex flex-col">
          <p class="text-text-attention font-medium">
            {{ $t('miniCart.splitDelivery.text', { amount: neededArea +' '+ unitDisplay }) }}
          </p>
          <p v-if="item.onHandValue > 0" class="text-text-subtle">
            {{ $t('miniCart.splitDelivery.description') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Product Item -->
    <div class="flex">
      <!-- Image -->
      <!-- Pointer-only duplicate of the name link below; hidden from AT -->
      <nuxt-link
        v-if="!disableInteraction"
        :to="productUrl(item.url)"
        aria-hidden="true"
        tabindex="-1"
        class="basis-70 h-70 desk:basis-80 desk:h-80 shrink-0 ratio-product relative flex items-center justify-center"
        :class="{ 'opacity-50': cartStore.isLoading }"
      >
        <div v-if="item.image">
          <nuxt-img
            preset="standard"
            provider="norce"
            loading="lazy"
            sizes="sm:124px"
            class="h-full w-full object-cover aspect-product"
            :src="item.image"
            :class="{ 'max-h-32 max-w-32': isCustomerSuccessProduct }"
            :alt="item.variantName"
          />
          <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
        </div>
        <div v-else class="relative">
          <img
            src="/icons/no-image.svg"
            class="aspect-product w-64 rounded-6"
            alt=""
          >
          <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
        </div>
        <div
          v-if="item.priceInfo.priceBeforeVat === '0' && !isCustomerSuccessProduct && !isSample"
          class="absolute -top-6 -left-6 bg-lightest h-28 w-28 rounded-full flex items-center justify-center"
        >
          <div class="h-24 w-24 rounded-full bg-bg-attention flex items-center justify-center">
            <img
              class="w-16 h-16"
              src="/icons/gift.svg"
              alt=""
            >
          </div>
        </div>
      </nuxt-link>

      <!-- Info + Price + Inputs -->
      <div
        class="w-full"
        :class="{ 'ml-8 desk:ml-16': !disableInteraction }"
      >
        <div
          class="type-xs-medium desk:type-sm-medium flex flex-col"
          :class="{
            'opacity-50': cartStore.isLoading,
            'text-criticalDark': usedPoints > 0,
            'w-full -mr-[28px]': isCustomerSuccessProduct
          }"
        >
          <!-- Row: name + partNo + price -->
          <div class="flex justify-between items-start gap-8">
            <div>
              <span
                v-if="disableInteraction && !isPalletDiscount"
                :class="{ 'text-text-subtle type-sm font-normal': isCheckoutSummary }"
              >
                {{ props.item.quantity }} x
              </span>
              <nuxt-link
                v-if="!disableInteraction"
                :to="productUrl(item.url)"
                :class="{
                  'type-headline-sm': !isCheckoutSummary,
                  'text-text-subtle type-sm font-normal': isCheckoutSummary
                }"
              >
                {{ item.variantName }}
                <span v-if="shouldUseArea && isCheckoutSummary">
                  = {{ neededArea }} {{ unitDisplay }}
                </span>
              </nuxt-link>
              <span
                v-else
                :class="{
                  'type-headline-sm': !isCheckoutSummary,
                  'text-text-subtle type-sm font-normal': isCheckoutSummary
                }"
              >
                {{ item.variantName }}
                <span v-if="shouldUseArea && isCheckoutSummary">
                  = {{ neededArea }} {{ unitDisplay }}
                </span>
              </span>

              <div v-if="!disableInteraction" class="type-xxs desk:type-xs text-text-subtle">
                {{ item.partNo }}
              </div>
            </div>

            <!-- Price -->
            <div class="text-right" :class="{ 'opacity-50': cartStore.isLoading }">
              <!-- Show total package price first with area price below for order proposals -->
              <div v-if="showPricePerArea">
                <div
                  class="type-headline-sm whitespace-nowrap"
                  :class="{ 'text-text-errorStrong': isPalletDiscount || usedPoints > 0 || (showStrikethroughTotal && !disableInteraction) }"
                >
                  {{ productPrice(item.priceInfoTotal, 'price', true) }}
                </div>
                <div
                  v-if="showStrikethroughTotal && !disableInteraction"
                  class="type-xs text-text-subtle whitespace-nowrap line-through"
                >
                  {{ productPrice(item.priceInfoTotal, 'priceBeforeDiscount', true) }}
                </div>

                <!-- Add price per area below if applicable -->
                <div v-if="shouldUseArea && pricePerArea" class="type-xs text-text-subtle mt-4">
                  {{ useLocalizedCurrency(pricePerArea) }}/{{ unitDisplay }}
                </div>
              </div>
              <!-- Show area price per unit when shouldUseArea is true (normal cart) -->
              <div v-else-if="shouldUseArea && areaPricePerUnit">
                <div
                  class="type-headline-sm whitespace-nowrap"
                  :class="{ 'text-text-errorStrong': isPalletDiscount || usedPoints > 0 || (showStrikethroughUnit && !disableInteraction) }"
                >
                  {{ useLocalizedCurrency(areaPricePerUnit) }}/{{ unitDisplay }}
                </div>
                <div
                  v-if="showStrikethroughUnit && !disableInteraction && areaPricePerUnitBeforeDiscount"
                  class="type-xs text-text-subtle whitespace-nowrap line-through"
                >
                  {{ useLocalizedCurrency(areaPricePerUnitBeforeDiscount) }}/{{ unitDisplay }}
                </div>
                <div class="type-xs text-text-subtle mt-2">
                  {{ productPrice(item.priceInfo, 'price', true) }}
                </div>
              </div>
              <!-- Show package/unit price when shouldUseArea is false -->
              <div v-else>
                <div
                  class="type-headline-sm whitespace-nowrap"
                  :class="{ 'text-text-errorStrong': isPalletDiscount || usedPoints > 0 || (showStrikethroughTotal && !disableInteraction) }"
                >
                  {{ productPrice(item.priceInfoTotal, 'price', true) }}
                </div>
                <div
                  v-if="showStrikethroughTotal && !disableInteraction"
                  class="type-xs text-text-subtle whitespace-nowrap line-through"
                >
                  {{ productPrice(item.priceInfoTotal, 'priceBeforeDiscount', true) }}
                </div>
              </div>

              <div
                v-if="salesRepOfferDiscount > 0 && (!isOrderProposal || (isOrderProposal && isCheckout))"
                class="type-xs text-text-subtle"
              >
                <span v-if="userStore.isSalesRepUser && salesRepOfferDiscount === 100">
                  {{ $t('checkout.gift') }}
                </span>
                <span v-else>
                  {{ $t('productPage.price.yourDiscount') }}
                  <span class="type-headline-xs text-text-errorStrong">-{{ salesRepOfferDiscount.toFixed() }}%</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Quantity -->
          <div
            v-if="isCustomerSuccessProduct"
            class="flex items-center w-full border border-light h-32 px-12 type-sm-medium mt-4"
          >
            {{ customerSuccessProductMessage }}
          </div>
          <div v-else-if="(!disableInteraction && !hideMeta)" class="flex items-top mt-6">
            <div v-if="(!globalStore.getIsB2c || item.isD2C) && !globalStore.getIsB2bSampleCheckout" class="relative flex items-center gap-12">
              <PackageToAreaConverter
                v-if="shouldUseArea"
                :key="`converterKey${converterKey}`"
                v-model="quantity"
                :input="item"
                design="minicart"
                :disable-edit="disableEdit"
                :disabled="disabled"
                :attr="{ min: minimumAmount }"
              />
              <TouchNumber
                v-else
                id="quantitySelector"
                v-model="quantity"
                :show-buttons-on-desktop="true"
                design="sm"
                class="max-w-120"
                :disable-edit="disableEdit"
                :disabled="disabled"
                :attr="{ min: minimumAmount }"
              />
            </div>

            <!-- Trashcan delete button -->
            <button
              v-if="!disableInteraction && !hideDelete && !disableEditing"
              class="basis-24 shrink-0 p-4"
              :class="{ 'ml-8': globalStore.getIsB2b }"
              type="button"
              :aria-label="$t('wcag.arialabel.removeFromCart', { name: item.variantName })"
              @click="remove"
            >
              <img
                src="/icons/trashcan.svg"
                class="w-16"
                alt=""
              >
            </button>
          </div>

          <!-- Stock error -->
          <div
            v-if="lastError && lastErrorCode === 422 && !disableInteraction && Array.isArray(lastError?.errors) && lastError?.errors?.filter((e) => e.partNo === item.partNo).length"
            class="text-criticalDark mt-12"
          >
            <small>{{ $t('stock.noLongerInStock') }}</small>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!disableInteraction && !hideMeta && (!globalStore.getIsB2c || item.isD2C) && !globalStore.getIsB2bSampleCheckout" class="flex items-center type-xs mt-12">
      <StockStatusSwitcher
        :cart-item="item"
        :is-checkout="isCheckout"
        :use-short-labels="true"
      />
    </div>

    <div
      v-if="(!isCheckout && !isCheckoutSummary && !isOrderProposal) && firstUnreachedQuantityBreak && firstUnreachedQuantityBreak.quantity - quantity > 0"
      class="type-xs mt-4 flex items-center gap-6"
    >
      <img
        src="/icons/info.svg"
        class="w-10"
        alt=""
      >
      {{ $t('miniCart.quantityBreak', {
        quantity: firstUnreachedQuantityBreak.quantity - quantity,
        discount: firstUnreachedQuantityBreak.discountPercentage
      }) }}
    </div>

    <div
      v-if="isPalletDiscount"
      class="type-xs text-text-base flex gap-6"
    >
      {{ $t('checkout.palletDiscount') }}
    </div>

    <div
      v-if="isOrderProposal && !isCheckout && salesRepOfferDiscount === 100"
      class="type-xs text-text-base flex gap-6"
    >
      <img
        src="/icons/gift.svg"
        class="w-10"
        :alt="$t('checkout.gift')"
      >
      {{ $t('checkout.gift') }}
    </div>

    <!-- * * * * Cart Actions * * * * -->
    <div
      class="flex flex-wrap gap-6 mt-12"
    >
      <!-- Shipping date -->
      <div v-if="showDatePickerInCheckout">
        <DatepickerV2
          :part-no="item.partNo"
          :date="preferredShippingDate"
          :default-date="defaultPreferredShippingDate"
          :selected-delivery-point="selectedDeliveryPoint"
          @update-preferred-shipping-date="onUpdatePreferredShippingDateDebounced"
        />
      </div>

      <!-- Product Comment -->
      <div v-if="!disableInteraction && isCheckout && !hideMeta && !globalStore.getIsB2c">
        <!-- Button state: shows when not editing -->
        <button
          v-if="!isEditingComment"
          class="inline-flex items-center gap-6 px-8 py-6 whitespace-nowrap bg-bg-base border border-border-base hover:bg-bg-subtle hover:border-border-strong transition-colors"
          type="button"
          @click="startEditingComment"
        >
          <GlobalIcon name="edit" size="xsmall" />
          <span class="type-headline-xs">{{ $t('checkout.item.comment') }}<span v-if="productComment">:</span></span>
          <span v-if="productComment" class="type-xs italic">{{ productComment }}</span>
          <GlobalIcon
            v-if="productComment"
            name="close-circle"
            size="xsmall"
          />
        </button>

        <!-- Input state: shows when editing -->
        <div
          v-else
          ref="commentEditContainer"
          class="inline-flex items-center gap-2"
        >
          <span class="type-headline-xs">{{ $t('checkout.item.comment') }}:</span>
          <Field
            v-slot="{ field }"
            type="text"
            name="productComment"
          >
            <input
              ref="commentInput"
              v-bind="field"
              v-model="tempComment"
              class="mx-4 px-8 py-6 bg-bg-base border border-border-base hover:border-border-strong type-xs min-w-200"
              :placeholder="$t('checkout.item.extraComments')"
            >
          </Field>
          <button
            class="inline-flex items-center justify-center w-28 h-28 desk:w-32 desk:h-32 bg-transparent hover:bg-bg-subtle transition-colors"
            type="button"
            @click="saveComment"
          >
            <GlobalIcon name="check" size="xsmall" />
          </button>
          <button
            class="inline-flex items-center justify-center w-28 h-28 desk:w-32 desk:h-32 bg-transparent hover:bg-bg-subtle transition-colors"
            type="button"
            @click="cancelEditingComment"
          >
            <GlobalIcon name="close" size="xsmall" />
          </button>
        </div>
      </div>

      <!-- Add Discounted Product -->
      <button
        v-if="showDiscountBtn"
        class="inline-flex items-center gap-6 px-8 py-6 type-headline-xs whitespace-nowrap bg-bg-base border border-border-base hover:bg-bg-subtle hover:border-border-strong transition-colors"
        type="button"
        @click="editProductDiscount(item)"
      >
        <GlobalIcon name="sale" size="xsmall" />
        {{ $t('checkout.orderProposal.editDiscount') }}
      </button>
    </div>

    <div v-if="usedPoints > 0" class="flex justify-between text-dark leading-single type-sm-medium mt-8">
      <div>{{ $t('pointshop.product.isPoints') }}</div>
      <div>{{ $t('pointshop.product.showPoints', { num: usedPoints }) }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCartStore } from '~/store/cart';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import type { CartApiItem } from '#constants/types/norce';
import useLinkReplacer from '~/composables/useLinkReplacer';
import debounce from '~/util/debounce';
import {
  NorceCartItemStatusId,
  NorceInfoTypesCodes
} from '#constants/norceCodes';
import useApiFetch from '~/composables/useApiFetch';
import usePrices from '~/composables/usePrices';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import { Field } from 'vee-validate';
import TouchNumber from '~/components/form-elements/TouchNumber.vue';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import useQuantityBreaks from '~/composables/useQuantityBreaks';
import NP from 'number-precision';
import StockStatusSwitcher from '~/components/product/StockStatusSwitcher.vue';
import {
  checkForFutureDelivery
} from '~/composables/useStockStatus';
import PackageToAreaConverter from '~/components/product-page/PackageToAreaConverter.vue';
import DatepickerV2 from '~/components/body/DatepickerV2.vue';

NP.enableBoundaryChecking(false);

const { lastError, lastErrorCode } = useApiFetch();
const cartStore = useCartStore();
const uiStore = useUiStore();
const userStore = useUserStore();
const globalStore = useGlobalContentStore();
const { $t } = useNuxtApp();
const { productUrl } = useLinkReplacer();
const { productPrice } = usePrices();

const showStrikethroughTotal = computed(() => {
  const info = props.item.priceInfoTotal;
  if (globalStore.getIsB2c) {
    return info.isCampaignPrice === true;
  }
  return info.price !== info.priceBeforeDiscount && (!globalStore.hideSRP || (info.discountPercentage ?? 0) > 0 || info.isCampaignPrice);
});

const showStrikethroughUnit = computed(() => {
  const info = props.item.priceInfo;
  if (globalStore.getIsB2c) {
    return info.isCampaignPrice === true;
  }
  return info.price !== info.priceBeforeDiscount && (!globalStore.hideSRP || (info.discountPercentage ?? 0) > 0 || info.isCampaignPrice);
});

const route = useRoute();

const isCheckout = computed(()=> {
  return route.name?.toString()?.endsWith('checkout') || route.name?.toString()?.endsWith('checkoutv2');
});

const props = defineProps<{
  item: CartApiItem,
  disableInteraction?: boolean,
  isOrderProposal?: boolean,
  showDiscountBtn?: boolean,
  hideDelete?: boolean,
  hideFavoriteAction?: boolean,
  hideMultiDiscount?: boolean,
  hideCrossDocking?: boolean,
  hideMeta?: boolean,
  isCheckoutSummary?: boolean,
  disableEdit?: boolean,
  disabled?: boolean,
  selectedDeliveryPoint?: 'store' | 'address' | 'consumer',
  showPricePerArea?: boolean,
}>();

const { areaPerPackage, unitDisplay, shouldUseArea, convertPackageToArea, convertPackagePriceToAreaPrice } = usePackageAreaConverter(toRef(() => props.item));
const { checkDiscountLevels, firstUnreachedQuantityBreak } = useQuantityBreaks(toRef(() => props.item));

onMounted(() => {
  checkDiscountLevels();
});

const quantity = ref<number>(props.item.quantity);
const selectQuantity = ref<number>(props.item.quantity);
const neededArea = ref(!shouldUseArea ? 0 : NP.times(quantity.value, (areaPerPackage.value ?? 0)));

const disableEditing = computed(() => {
  if (!userStore.isSalesRepUser && !userStore.isCustomerSuccessUser && props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.SalesRepDiscount)?.value) {
    return true;
  }

  return false;
});

const usedPoints = computed(()=>{
  const infoType = props.item.infoTypes.find((f) => f.name === NorceInfoTypesCodes.PointShopValue);
  if (infoType) {
    return parseInt(infoType.value);
  }
  return -1;
});

const isCustomerSuccessProduct = computed(() => {
  return props.item.infoTypes.find(infoType => infoType.name === 'isCustomerSuccessProduct')?.value === 'true';
});

const customerSuccessProductMessage = computed(() => {
  return props.item.infoTypes.find(infoType => infoType.name === 'customerSuccessProductMessage')?.value || '';
});

const isSample = computed(() => {
  return (props.item.categories.filter(c => c.code === 'Samples')?.length ?? 0) > 0;
});

const isPalletDiscount = computed(() => {
  return props.item.partNo === globalStore.palletDiscountPartNo;
});

const remove = () => {
  if (isCustomerSuccessProduct.value) {
    cartStore.updateCustomerSuccessProduct({ partNo: props.item.partNo, message: '' });
  } else {
    quantity.value = 0;
    updateQuantity(0);
  }
};

const isExpiring = computed(()=> {
  return props.item.statusId === NorceCartItemStatusId.Expiring;
});

const testoutput = ref(null) as any;

// * * * * * * * UPDATE CART
// i hade so much trouble with the debounce function, we use a regular timer
const timer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const isUpdating = ref(false);

watch(quantity, (value)=>{
  if (shouldUseArea.value) {
    neededArea.value = NP.times(quantity.value, areaPerPackage.value ?? 0);
  }
  if (!isCheckout.value) {
    checkDiscountLevels();
  }

  // Prevent duplicate updates
  if (isUpdating.value) {
    return;
  }

  clearTimeout(timer.value);
  timer.value = setTimeout(()=> updateQuantity(value), 500);
});

const converterKey = ref(1);

/**
 * Updates the quantity of an item in the cart based on the given input value.
 * This function includes validations and handles scenarios such as expiring products,
 * checking future delivery availability, and updating the cart through the API.
 *
 * @description
 * - If the current quantity matches the input, no action is taken.
 * - For products marked as expiring, it validates the availability using the API before proceeding.
 * - Displays appropriate error messages when quantity updates are restricted due to stock or expiration.
 * - Resets the quantity to its original value if the update fails.
 * - Updates the cart with the new quantity through the store when all validations pass.
 */
const updateQuantity = async(quantityInput: number) => {
  if (props.item.quantity === quantityInput || isUpdating.value) {
    return;
  }

  isUpdating.value = true;

  // If the product is expiring we need to check the api before
  // accepting any changes - this assumes a product can only be in cart on one lineNo, not duplicates
  if (isExpiring.value) {
    let needed = quantityInput;
    if (shouldUseArea.value) {
      needed = convertPackageToArea(quantityInput) || 1;
    }
    const futureRes = await checkForFutureDelivery(
      props.item.partNo,
      needed,
      0,
      shouldUseArea.value,
      unitDisplay,
      true
    );

    if (futureRes && futureRes.lockBuyButton) {
      testoutput.value = futureRes;
      let errorMsg = $t('stock.expiring');
      if (futureRes.totalAvailable && futureRes.unitDisplay) {
        errorMsg = $t('stock.expiring.max', {
          num: futureRes.totalAvailable,
          unit: futureRes.unitDisplay,
        });
      }

      uiStore.setTemporaryError(errorMsg, 5000);

    }
  }

  // Check so it's not under minimum value
  if (quantity.value < minimumAmount.value) {
    console.warn('less then minimum');
    // Resets quantity to before user interaction
    quantity.value = props.item.quantity;
    neededArea.value = NP.times(quantity.value, areaPerPackage.value ?? 0);
    isUpdating.value = false;
    return;
  }

  console.log('Updating');

  const res = await cartStore.updateCart(
    props.item.partNo,
    quantityInput,
    props.item.lineNo,
    false,
    props.item,
    true,
    '',
    props.item.priceListLocked
  );

  if (!res) {
    quantity.value = props.item.quantity;
    cartStore.isLoading = false;
  } else {
    const actualQuantity = cartStore.getQuantityFromLineNo(props.item.lineNo);
    if (actualQuantity !== quantityInput) {
      // This line in the cart didn't update to the requested quantityInput
      // This most likely happened because the backend split our order into multiple lines
      // Update the local quantity to reflect what the API actually returned for this line
      quantity.value = actualQuantity;
      if (shouldUseArea.value) {
        neededArea.value = NP.times(actualQuantity, areaPerPackage.value ?? 0);
      }
      converterKey.value++;
    }
  }

  isUpdating.value = false;
};

const editProductDiscount = (async(item: CartApiItem) => {
  uiStore.setCurrentlyEditingItem(item);
  uiStore.setShowEditProductDiscountModal(true);
});

const salesRepOfferDiscount = computed((): number => {
  return Number(props.item.infoTypes?.filter(i => i.name === NorceInfoTypesCodes.SalesRepDiscount)[0]?.value) * 100 || 0;
});

// * * * * * * * minimum amount
const minimumAmount = computed(()=> {
  const min = props.item.infoTypes.find(f => f.name === 'salesRepMinQuantity');

  if (min) {
    return parseInt(min.value);
  }
  return 0;

});

// * * * * * * * UPDATE CART
const preferredShippingDate = ref(props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.PreferredShippingDate && i.value !== '')?.value);

const defaultPreferredShippingDate = ref(props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value);
const defaultPreferredShippingDateComputed = computed(() =>props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value);

const showDatePickerInCheckout = computed(() => {
  return globalStore.getIsB2b && defaultPreferredShippingDateComputed.value !== 'false' && isCheckout.value;
});

// * * * * * * *  Watch if props are changed
watch(
  () => props.item,
  () => {
    if (!isUpdating.value) {
      quantity.value = props.item.quantity;
      selectQuantity.value = props.item.quantity;
    }
    preferredShippingDate.value = props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.PreferredShippingDate && i.value !== '')?.value;
    defaultPreferredShippingDate.value = props.item.infoTypes.find((i) => i.name === NorceInfoTypesCodes.DefaultPreferredShippingDate && i.value !== '')?.value;

    if (!isCheckout.value) {
      checkDiscountLevels();
    }
  }
);

// Watch specifically for quantity changes from backend
watch(
  () => props.item.quantity,
  (newQuantity) => {
    if (newQuantity !== quantity.value && !isUpdating.value) {
      quantity.value = newQuantity;
      selectQuantity.value = newQuantity;
      if (shouldUseArea.value) {
        neededArea.value = NP.times(newQuantity, areaPerPackage.value ?? 0);
      }
    }
  }
);

const onUpdatePreferredShippingDateDebounced = debounce(async(date: string) => {
  await cartStore.updateCartItemInfoType(props.item, NorceInfoTypesCodes.PreferredShippingDate, date);

  if (cartStore.cart.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.MergeShipments && i.value === 'true')?.length) {
    await cartStore.updateCartInfoType(NorceInfoTypesCodes.MergeShipments, '');
  }
  setTimeout(() => {
    cartStore.toggleModFee();
  }, 200);
  
}, 500);

const productComment = ref(props.item.infoTypes?.filter((i) => i.name === NorceInfoTypesCodes.ShippingComment)[0]?.value ?? '');
const tempComment = ref('');
const isEditingComment = ref(false);
const commentInput = ref<HTMLInputElement | null>(null);
const commentEditContainer = ref<HTMLDivElement | null>(null);

const startEditingComment = (event: Event) => {
  event.stopPropagation();
  tempComment.value = productComment.value;
  isEditingComment.value = true;
  nextTick(() => {
    commentInput.value?.focus();
  });
};

const saveComment = async() => {
  productComment.value = tempComment.value;
  await cartStore.updateCartItemInfoType(props.item, NorceInfoTypesCodes.ShippingComment, productComment.value);
  isEditingComment.value = false;
};

const cancelEditingComment = () => {
  tempComment.value = '';
  isEditingComment.value = false;
};

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (isEditingComment.value && commentEditContainer.value && !commentEditContainer.value.contains(event.target as Node)) {
    cancelEditingComment();
  }
};

onMounted(() => {
  // Delay adding the event listener to prevent immediate triggering
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Compute area prices for display
const areaPricePerUnit = computed(() => {
  if (!shouldUseArea.value) {
    return null;
  }

  const priceValue = globalStore.getDisplayIncVat
    ? props.item.priceInfo.price
    : props.item.priceInfo.priceBeforeVat;

  return convertPackagePriceToAreaPrice(priceValue);
});

const areaPricePerUnitBeforeDiscount = computed(() => {
  if (!shouldUseArea.value) {
    return null;
  }

  const priceValue = globalStore.getDisplayIncVat
    ? props.item.priceInfo.priceBeforeDiscount
    : props.item.priceInfo.priceBeforeDiscountBeforeVat;

  return convertPackagePriceToAreaPrice(priceValue);
});

const pricePerArea = computed(() => {
  if (!shouldUseArea.value || !props.showPricePerArea || !props.item.priceInfo?.priceBeforeVat) {
    return null;
  }
  const price = parseFloat(props.item.priceInfo.priceBeforeVat.replaceAll(',', ''));
  return convertPackagePriceToAreaPrice(price);
});

</script>
